<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="faq-modal">
      <div class="modal-header">
        <h2 class="text-lg font-medium text-slate-900">
          {{ isEditing ? $t('BUSINESS_DATA.FAQ.EDIT') : $t('BUSINESS_DATA.FAQ.ADD_NEW') }}
        </h2>
      </div>
      
      <form @submit.prevent="onSubmit" class="modal-form">
        <!-- Question field -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.FAQ.QUESTION') }} *
          </label>
          <textarea
            v-model="formData.question"
            :placeholder="$t('BUSINESS_DATA.FAQ.QUESTION_PLACEHOLDER')"
            class="form-textarea"
            rows="3"
            required
            maxlength="100"
          />
          <div class="form-help text-right">
            <span :class="formData.question.length > 80 ? 'text-red-500' : 'text-gray-500'">
              {{ formData.question.length }}/100
            </span>
          </div>
        </div>
        
        <!-- Answer field -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.FAQ.ANSWER') }} *
          </label>
          <textarea
            v-model="formData.answer"
            :placeholder="$t('BUSINESS_DATA.FAQ.ANSWER_PLACEHOLDER')"
            class="form-textarea"
            rows="4"
            required
            maxlength="2000"
          />
          <div class="form-help text-right">
            <span :class="formData.answer.length > 1800 ? 'text-red-500' : 'text-gray-500'">
              {{ formData.answer.length }}/2000
            </span>
          </div>
        </div>
        
        <!-- Category field -->
        <div class="form-row">
          <label class="form-label">
            {{ $t('BUSINESS_DATA.FAQ.CATEGORY') }}
          </label>
          <CategorySelector
            v-model="formData.business_category_id"
            :categories="businessCategories"
            category-type="faq"
            :placeholder="$t('BUSINESS_DATA.FAQ.CATEGORY_PLACEHOLDER')"
            @category-created="onCategoryCreated"
          />
          <div class="form-help">
            {{ $t('BUSINESS_DATA.FAQ.CATEGORY_HELP') }}
          </div>
        </div>
        
        <!-- Images field -->
        <div class="form-row">
          <ImagePicker
            v-model="formData.image_item_ids"
            :account-id="currentAccountId"
          />
        </div>
        
        <!-- Active toggle -->
        <div class="form-row">
          <div class="flex items-center">
            <woot-switch
              :model-value="formData.is_active"
              @input="formData.is_active = $event"
            />
            <span class="ml-3 text-sm font-medium text-gray-700">
              {{ $t('BUSINESS_DATA.FAQ.ACTIVE') }}
            </span>
          </div>
          <div class="form-help">
            {{ $t('BUSINESS_DATA.FAQ.ACTIVE_HELP') }}
          </div>
        </div>
        
        <!-- Actions -->
        <div class="modal-footer">
          <woot-button
            type="button"
            variant="clear"
            @click="onClose"
          >
            {{ $t('COMMON.CANCEL') }}
          </woot-button>
          
          <woot-button
            type="submit"
            color-scheme="primary"
            :loading="isSaving"
            :disabled="!isFormValid"
          >
            {{ isEditing ? $t('COMMON.UPDATE') : $t('COMMON.CREATE') }}
          </woot-button>
        </div>
      </form>
    </div>
  </woot-modal>
</template>

<script>
import { useAlert } from 'dashboard/composables';
import ImagePicker from './ImagePicker.vue';
import CategorySelector from './CategorySelector.vue';

export default {
  name: 'FaqModal',
  components: { ImagePicker, CategorySelector },
  
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    faq: {
      type: Object,
      default: null,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    businessCategories: {
      type: Array,
      default: () => [],
    },
  },
  
  data() {
    return {
      isSaving: false,
      formData: {
        question: '',
        answer: '',
        category: '',
        business_category_id: null,
        is_active: true,
        image_item_ids: [],
      },
    };
  },
  
  computed: {
    isEditing() {
      return !!this.faq;
    },
    
    isFormValid() {
      return this.formData.question.trim() && 
             this.formData.answer.trim() &&
             this.formData.question.length <= 5000 &&
             this.formData.answer.length <= 5000;
    },
    
    existingCategories() {
      return this.categories.filter(cat => cat && cat.trim());
    },
    
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },
  },
  
  watch: {
    faq: {
      immediate: true,
              handler(newFaq) {
          if (newFaq) {
            this.formData = {
              question: newFaq.question || '',
              answer: newFaq.answer || '',
              category: newFaq.category || '',
              business_category_id: newFaq.business_category_id || null,
              is_active: newFaq.is_active !== undefined ? newFaq.is_active : true,
              image_item_ids: newFaq.image_item_ids || [],
            };
          } else {
            this.resetForm();
          }
        },
    },
    
    show(newShow) {
      if (!newShow) {
        this.resetForm();
      }
    },
    
    businessCategories: {
      immediate: true,
      handler(newCategories) {
        console.log('FaqModal received business categories:', newCategories);
      },
    },
  },
  
  methods: {
    resetForm() {
      this.formData = {
        question: '',
        answer: '',
        category: '',
        business_category_id: null,
        is_active: true,
        image_item_ids: [],
      };
    },
    
    onCategoryCreated(newCategory) {
      this.$emit('category-created', newCategory);
    },
    
    async onSubmit() {
      if (!this.isFormValid) {
        return;
      }
      
      this.isSaving = true;
      try {
        const payload = {
          question: (this.formData.question || '').trim(),
          answer: (this.formData.answer || '').trim(),
          category: (this.formData.category || '').trim() || null,
          business_category_id: this.formData.business_category_id,
          is_active: this.formData.is_active,
          image_item_ids: this.formData.image_item_ids || [],
        };
        
        if (this.isEditing) {
          await window.axios.put(
            `/api/v1/accounts/${this.currentAccountId}/business_faqs/${this.faq.id}`,
            payload
          );
        } else {
          await window.axios.post(
            `/api/v1/accounts/${this.currentAccountId}/business_faqs`,
            payload
          );
        }
        
        this.$emit('saved');
      } catch (error) {
        console.error('Error saving FAQ:', error);
        console.error('Error response:', error?.response?.data);
        
        let errorMessage = this.$t('BUSINESS_DATA.ERROR.SAVE_FAILED');
        
        if (error?.response?.data?.errors) {
          errorMessage = error.response.data.errors.join(', ');
        } else if (error?.response?.data?.message) {
          errorMessage = error.response.data.message;
        }
        
        useAlert(errorMessage, 'error');
      } finally {
        this.isSaving = false;
      }
    },
    
    onClose() {
      this.$emit('close');
    },
  },
  
  emits: ['close', 'saved', 'category-created'],
};
</script>

<style lang="scss" scoped>
.faq-modal {
  width: 100%;
  max-width: 600px;
  padding: 24px;
}

.modal-header {
  margin-bottom: var(--space-large);
  padding-top: 8px;
  
  h2 {
    margin: 0;
    color: var(--color-heading);
    font-size: var(--font-size-large);
  }
}

.modal-form {
  .form-row {
    margin-bottom: var(--space-normal);
    
    .form-label {
      display: block;
      margin-bottom: var(--space-smaller);
      font-weight: var(--font-weight-medium);
      color: var(--color-heading);
    }
    
    .form-input,
    .form-textarea {
      width: 100%;
      padding: var(--space-small);
      border: 1px solid var(--color-border);
      border-radius: var(--border-radius-normal);
      font-size: var(--font-size-default);
      
      &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 1px var(--color-primary);
      }
    }
    
    .form-textarea {
      resize: vertical;
      min-height: 80px;
    }
    
    .form-help {
      margin-top: var(--space-smaller);
      font-size: var(--font-size-small);
      color: var(--color-text-light);
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-small);
  margin-top: var(--space-large);
  padding-top: var(--space-normal);
  border-top: 1px solid var(--color-border-light);
}

@media (max-width: 768px) {
  .faq-modal {
    max-width: 100%;
  }
}
</style>
